import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import CarClass from '../../../types/car-class/CarClass';
import CarClassWriteDTO from '../../../types/car-class/CarClassWriteDTO';
import Page from '../../../types/shared/Page';
import { BaseService } from './base.service';

@Injectable({
    providedIn: 'root'
})
export class CarClassService {

    private RESOURCE: string = 'car-class';

    constructor(private baseService: BaseService) {
    }

    /**
     * Fetches the car classes from the backend.
     *
     * @param search The search query to filter the car classes by.
     * @param page The page number to fetch.
     * @param size The number of elements to fetch per page.
     * @return The car classes.
     */
    public async getCarClasses(search?: string, page: number = 0, size: number = 10): Promise<Page<CarClass>> {
        let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
        if (search) {
            params = params.set('search', search);
        }

        return await this.baseService.get<Page<CarClass>>(this.RESOURCE, params);
    }

    /**
     * Fetches multiple car classes from the backend.
     *
     * @param ids The IDs of the car classes to fetch.
     * @return The car classes.
     */
    public async getMultipleCarClasses(ids: number[]): Promise<CarClass[]> {
        const params = new HttpParams().set('ids', ids.join(','));

        return await this.baseService.get<CarClass[]>(this.RESOURCE + '/multiple', params);
    }

    /**
     * Fetches all car classes from the backend.
     *
     * @return The car classes.
     */
    public async getAllCarClasses(): Promise<CarClass[]> {
        return await this.baseService.get<CarClass[]>(this.RESOURCE + '/all');
    }

    /**
     * Fetches a car class from the backend.
     *
     * @param id The ID of the car class to fetch.
     * @return The car class.
     */
    public async getCarClass(id: number): Promise<CarClass> {
        return await this.baseService.get<CarClass>(this.RESOURCE + '/' + id);
    }

    /**
     * Creates a car class in the backend.
     *
     * @param carClass The car class to create.
     * @return The response from the request.
     */
    public async createCarClass(carClass: CarClassWriteDTO): Promise<void> {
        return await this.baseService.post<void>(this.RESOURCE, carClass);
    }

    /**
     * Updates a car class in the backend.
     *
     * @param carClass The car class to update.
     * @return The response from the request.
     */
    public async updateCarClass(carClass: CarClassWriteDTO): Promise<void> {
        return await this.baseService.put<void>(this.RESOURCE + '/' + carClass.id, carClass);
    }

    /**
     * Deletes a car class from the backend.
     *
     * @param id The ID of the car class to delete.
     * @return The response from the request.
     */
    public async deleteCarClass(id: number): Promise<void> {
        return await this.baseService.delete<void>(this.RESOURCE + '/' + id);
    }
}
